import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../Config.js";
import InventoryManager from './InventoryManager';
import InventoryModal from './InventoryModal.js';
import styles from './InventoryUpdate.module.css';

const InventoryUpdate = () => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showFieldSelectionModal, setShowFieldSelectionModal] = useState(false);
  const [itemUpdateList, setItemUpdateList] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);

  // Drives the interactive loading flow.
  // 'idle' | 'uploading' | 'processing' | 'done' | 'error'
  const [uploadStage, setUploadStage] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState(0);

  const availableFields = [
    'Item Code',
    'Item Description',
    'Item Type',
    'Item Color',
    'Item Size',
    'School Name',
    'Price',
    'Wholesale Price',
    'Quantity'
  ];

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');
    return {
      user: user ? JSON.parse(user) : null,
      token: token || null
    };
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add(styles.dragover);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove(styles.dragover);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove(styles.dragover);
  };

  const handleUpload = () => {
    if (!file) return;
    setShowFieldSelectionModal(true);
  };

  const handleFieldSelection = (field) => {
    setSelectedFields(prev =>
      prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const toggleSelectAll = () => {
    setSelectedFields(prev =>
      prev.length === availableFields.length ? [] : [...availableFields]
    );
  };

  const handleConfirmUpload = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to update.');
      return;
    }

    const userData = getUserData();
    const storeId = userData.user?.storeId;

    setIsUploading(true);
    setShowFieldSelectionModal(false);
    setUploadStage('uploading');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    const requestData = {
      user: userData.user,
      token: userData.token,
      storeId: storeId,
      itemUpdateValueList: selectedFields
    };

    formData.append('requestData', new Blob([JSON.stringify(requestData)], {
      type: 'application/json'
    }));

    try {
      const response = await axios.post(`${API_BASE_URL}/inventory/edit/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
            // Once the bytes are sent, the server is crunching the data.
            if (percent >= 100) setUploadStage('processing');
          } else {
            setUploadStage('processing');
          }
        },
      });

      setUploadStage('done');
      setItemUpdateList(response.data.payload);

      // Brief beat so the user sees the success state before the review modal.
      setTimeout(() => {
        setShowItemModal(true);
        resetUploadState();
      }, 650);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStage('error');
    }
  };

  const resetUploadState = () => {
    setIsUploading(false);
    setUploadStage('idle');
    setUploadProgress(0);
    setFile(null);
    setSelectedFields([]);
  };

  const handleRetry = () => {
    setUploadStage('idle');
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleCancelUpload = () => {
    setShowFieldSelectionModal(false);
    setSelectedFields([]);
  };

  const handleClear = () => {
    setFile(null);
  };

  const steps = [
    { key: 'uploading', label: 'Uploading file' },
    { key: 'processing', label: 'Processing data' },
    { key: 'done', label: 'Ready to review' }
  ];

  const stageIndex = { uploading: 0, processing: 1, done: 2, error: 0 }[uploadStage] ?? -1;

  return (
    <div className={styles.inventoryUpdateContainer}>
      {/* Left half - Inventory Upload */}
      <div className={styles.inventoryUploadSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Inventory Upload</h2>
          <p className={styles.sectionSubtitle}>
            Upload an Excel sheet to review and apply bulk changes to your stock.
          </p>
        </div>

        {!isUploading && (
          <>
            {!file ? (
              <label
                className={styles.dropZone}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
              >
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".xlsx, .xls"
                  style={{ display: 'none' }}
                />
                <span className={styles.dropIcon} aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 16V4" />
                    <path d="m7 9 5-5 5 5" />
                    <path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
                  </svg>
                </span>
                <p className={styles.dropTitle}>Drag &amp; drop your Excel file</p>
                <p className={styles.dropHint}>or click to browse · .xlsx, .xls</p>
              </label>
            ) : (
              <div className={styles.fileInfo}>
                <span className={styles.fileIcon} aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.7"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                  </svg>
                </span>
                <div className={styles.fileMeta}>
                  <span className={styles.fileName}>{file.name}</span>
                  <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                </div>
                <button className={styles.removeBtn} onClick={handleClear}>Remove</button>
              </div>
            )}

            <div className={styles.buttonGroup}>
              <button
                className={styles.primaryBtn}
                onClick={handleUpload}
                disabled={!file}
              >
                Upload &amp; Review
              </button>
            </div>
          </>
        )}

        {/* Interactive loading flow */}
        {isUploading && (
          <div className={styles.progressPanel}>
            {uploadStage !== 'error' ? (
              <>
                <div className={styles.progressRingWrap}>
                  {uploadStage === 'done' ? (
                    <span className={styles.successCheck} aria-hidden="true">
                      <svg width="34" height="34" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.4"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                  ) : uploadStage === 'uploading' ? (
                    <div className={styles.progressRing}>
                      <svg viewBox="0 0 120 120" className={styles.ringSvg}>
                        <circle className={styles.ringTrack} cx="60" cy="60" r="52" />
                        <circle
                          className={styles.ringFill}
                          cx="60" cy="60" r="52"
                          style={{ strokeDashoffset: 327 - (327 * uploadProgress) / 100 }}
                        />
                      </svg>
                      <span className={styles.ringLabel}>{uploadProgress}%</span>
                    </div>
                  ) : (
                    <div className={styles.spinner} aria-label="Processing" />
                  )}
                </div>

                <p className={styles.progressTitle}>
                  {uploadStage === 'uploading' && 'Uploading your file…'}
                  {uploadStage === 'processing' && 'Processing inventory data…'}
                  {uploadStage === 'done' && 'All set!'}
                </p>
                <p className={styles.progressSub}>
                  {uploadStage === 'uploading' && 'Please keep this window open.'}
                  {uploadStage === 'processing' && 'Matching items and validating changes.'}
                  {uploadStage === 'done' && 'Opening your review window…'}
                </p>

                <ol className={styles.stepper}>
                  {steps.map((step, i) => {
                    const state =
                      i < stageIndex ? 'complete' : i === stageIndex ? 'active' : 'pending';
                    return (
                      <li key={step.key} className={`${styles.step} ${styles[state]}`}>
                        <span className={styles.stepDot}>
                          {state === 'complete' ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                              stroke="currentColor" strokeWidth="3"
                              strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          ) : (
                            <span className={styles.stepIndex}>{i + 1}</span>
                          )}
                        </span>
                        <span className={styles.stepLabel}>{step.label}</span>
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : (
              <div className={styles.errorState}>
                <span className={styles.errorIcon} aria-hidden="true">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4" />
                    <path d="M12 16h.01" />
                  </svg>
                </span>
                <p className={styles.progressTitle}>Upload failed</p>
                <p className={styles.progressSub}>
                  Something went wrong while uploading. Please try again.
                </p>
                <div className={styles.errorActions}>
                  <button className={styles.secondaryBtn} onClick={resetUploadState}>Start over</button>
                  <button className={styles.primaryBtn} onClick={handleRetry}>Try again</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right half - Inventory Manager */}
      <div className={styles.inventoryManagerSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Inventory Manager</h2>
          <p className={styles.sectionSubtitle}>Download a current snapshot of your stock list.</p>
        </div>
        <InventoryManager />
      </div>

      {/* Field Selection Modal */}
      {showFieldSelectionModal && (
        <div className={styles.modalOverlay} onClick={handleCancelUpload}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Select Fields to Update</h3>
              <p className={styles.modalHeaderSub}>
                Only the fields you choose will be changed from the uploaded file.
              </p>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.selectAllRow}>
                <button className={styles.selectAllBtn} onClick={toggleSelectAll}>
                  {selectedFields.length === availableFields.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className={styles.fieldCheckboxContainer}>
                {availableFields.map(field => (
                  <label key={field} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field)}
                      onChange={() => handleFieldSelection(field)}
                      className={styles.checkbox}
                    />
                    <span>{field}</span>
                  </label>
                ))}
              </div>
              <div className={styles.selectedCount}>
                {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={handleCancelUpload} className={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                className={styles.confirmButton}
                disabled={selectedFields.length === 0}
              >
                Confirm &amp; Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemModal && (
        <InventoryModal data={itemUpdateList} onClose={() => setShowItemModal(false)} />
      )}
    </div>
  );
};

export default InventoryUpdate;